import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import AddPaymentModal from './AddPaymentModal';
import ViewPaymentsModal from './ViewPaymentsModal';
import UpdatePurchaseStatusModal from './UpdatePurchaseStatusModal';
import PurchaseNotificationModal from './PurchaseNotificationModal';

const ListPurchases = () => {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [openActionId, setOpenActionId] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showViewPaymentsModal, setShowViewPaymentsModal] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  useEffect(() => {
    fetchPurchases();
  }, [currentPage, perPage, searchTerm]);

  const fetchPurchases = async () => {
    setIsLoading(true);
    try {
      try {
        const userDetails = await apiService.getUserDetails();
        const userData = userDetails.data || userDetails;
        setLoggedInUser(userData);
      } catch (err) {
        console.warn('Could not fetch user details:', err);
      }
      
      const data = await apiService.getPurchases({ page: currentPage, per_page: perPage, search: searchTerm });
      setPurchases(Array.isArray(data) ? data : (data.data || []));
    } catch (err) {
      console.error('Failed to load purchases', err);
      setPurchases([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPayment = (purchase) => {
    setSelectedPurchase(purchase);
    setShowAddPaymentModal(true);
    setOpenActionId(null);
  };

  const handleViewPayments = (purchase) => {
    setSelectedPurchase(purchase);
    setShowViewPaymentsModal(true);
    setOpenActionId(null);
  };

  const handleUpdateStatus = (purchase) => {
    setSelectedPurchase(purchase);
    setShowUpdateStatusModal(true);
    setOpenActionId(null);
  };

  const handleShowNotification = (purchase) => {
    setSelectedPurchase(purchase);
    setShowNotificationModal(true);
    setOpenActionId(null);
  };

  const resolveAddedBy = (purchase) => {
    // 1. Try created_by_user object (Relation)
    if (purchase.created_by_user) {
      return `${purchase.created_by_user.first_name || ''} ${purchase.created_by_user.last_name || ''}`.trim() || purchase.created_by_user.username;
    }
    
    // 2. Try created_by object (Flattened or alternate name)
    if (typeof purchase.created_by === 'object' && purchase.created_by) {
      return `${purchase.created_by.first_name || ''} ${purchase.created_by.last_name || ''}`.trim() || purchase.created_by.username;
    }

    // 3. Session Fallback: If IDs match
    if (loggedInUser && (purchase.created_by === loggedInUser.id || purchase.created_by_id === loggedInUser.id)) {
      return `${loggedInUser.first_name || ''} ${loggedInUser.last_name || ''}`.trim() || loggedInUser.username;
    }

    // 4. Other fields
    return purchase.added_by_name || purchase.created_by_name || 'N/A';
  };

  const handleExport = (format) => {
    if (!purchases || purchases.length === 0) {
      alert('No data to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['Action', 'Date', 'Reference No', 'Location', 'Supplier', 'Purchase Status', 'Payment Status', 'Grand Total', 'Payment Due', 'Added By'];
    const rows = purchases.map(p => [
      '', // Action placeholder
      p.transaction_date || 'N/A',
      p.ref_no || 'N/A',
      p.location?.name || 'N/A',
      p.supplier?.name || p.contact?.name || 'N/A',
      p.status || 'Pending',
      p.payment_status || 'Due',
      parseFloat(p.final_total || 0).toFixed(2),
      `P: ${parseFloat(p.payment_due || 0).toFixed(2)}${p.return_transaction ? ` / R: ${parseFloat(p.return_transaction.amount_due || 0).toFixed(2)}` : ''}`,
      resolveAddedBy(p)
    ]);

    const title = 'Purchases List';
    const filename = 'purchases_list';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handlePrint = () => { printPage(); setShowExportDropdown(false); };
  const handleColumnVisibility = () => { alert('Column visibility settings'); setShowExportDropdown(false); };

  return (
    <div className="products-container">
      <div className="main-card">
        <div className="product-tabs">
          <div className="product-tab active">
            <i className="fa fa-list"></i> List Purchases
          </div>
        </div>

        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input" />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)} title="Open Filters">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={() => navigate('/purchases/add')}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="loading-sales">
            <h3>⏳ Loading Purchases...</h3>
            <p>Fetching purchases data from server...</p>
          </div>
        ) : purchases.length === 0 ? (
          <div className="no-sales">
            <h3>📊 No Purchases Found</h3>
            <p>No purchases have been recorded yet.</p>
          </div>
        ) : (
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Date</th>
                  <th>Reference No</th>
                  <th>Location</th>
                  <th>Supplier</th>
                  <th>Purchase Status</th>
                  <th>Payment Status</th>
                  <th>Grand Total</th>
                  <th>Payment Due</th>
                  <th>Added By</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map(purchase => (
                  <tr key={purchase.id}>
                    <td className="actions-cell">
                      <div className="action-dropdown-container">
                        <button className="action-btn-trigger" onClick={() => setOpenActionId(openActionId === purchase.id ? null : purchase.id)}>
                          Actions <span className="arrow-down">▼</span>
                        </button>
                        {openActionId === purchase.id && (
                            <div className="action-dropdown-menu">
                              <button onClick={() => alert('View functionality coming soon!')}><i className="fa fa-eye"></i> View</button>
                              <button onClick={() => alert('Print functionality coming soon!')}><i className="fa fa-print"></i> Print</button>
                              <button onClick={() => alert('Edit functionality coming soon!')}><i className="fa fa-edit"></i> Edit</button>
                              <button onClick={() => alert('Delete functionality coming soon!')} className="delete-btn"><i className="fa fa-trash"></i> Delete</button>
                              <button onClick={() => alert('Labels functionality coming soon!')}><i className="fa fa-barcode"></i> Labels</button>
                              
                               <div className="divider"></div>
                              
                              <button onClick={() => handleViewPayments(purchase)}><i className="fa fa-money"></i> View Payments</button>
                              {purchase.payment_status?.toLowerCase() !== 'paid' && (
                                  <button onClick={() => handleAddPayment(purchase)}>
                                  <i className="fa fa-money-bill-wave"></i> Add Payment
                                </button>
                              )}
                              <button onClick={() => handleUpdateStatus(purchase)}><i className="fa fa-check-square-o"></i> Update Status</button>
                              <button onClick={() => handleShowNotification(purchase)}><i className="fa fa-envelope"></i> Items Pending Notification</button>
                              <button onClick={() => alert('Purchase Return functionality coming soon!')}><i className="fa fa-undo"></i> Purchase Return</button>
                            </div>
                        )}
                      </div>
                    </td>
                    <td>{purchase.transaction_date || 'N/A'}</td>
                    <td>{purchase.ref_no || 'N/A'}</td>
                    <td>{purchase.location?.name || 'N/A'}</td>
                    <td>{purchase.supplier?.name || purchase.contact?.name || 'N/A'}</td>
                    <td><span className={`status-badge ${purchase.status?.toLowerCase()}`}>{purchase.status || 'Pending'}</span></td>
                    <td><span className={`status-badge ${purchase.payment_status?.toLowerCase()}`}>{purchase.payment_status || 'Due'}</span></td>
                    <td>${parseFloat(purchase.final_total || 0).toFixed(2)}</td>
                    <td className="payment-due-cell">
                      <div style={{ fontSize: '0.85rem' }}>
                        <span style={{ color: '#64748b' }}>P:</span> ${parseFloat(purchase.payment_due || 0).toFixed(2)}
                      </div>
                      {purchase.return_transaction && parseFloat(purchase.return_transaction.amount_due) > 0 && (
                        <div style={{ fontSize: '0.85rem', marginTop: '2px' }}>
                          <span style={{ color: '#ef4444' }}>R:</span> ${parseFloat(purchase.return_transaction.amount_due).toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td>{resolveAddedBy(purchase)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                <div className="filter-group"><label>Status:</label><select><option value="all">All Status</option></select></div>
                <div className="filter-group"><label>Payment Status:</label><select><option value="all">All</option></select></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Close</button>
              <button className="modal-btn primary-btn">Apply Filters</button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modals */}
      {showAddPaymentModal && selectedPurchase && (
        <AddPaymentModal
          show={showAddPaymentModal}
          onClose={() => setShowAddPaymentModal(false)}
          transaction={selectedPurchase}
          onSuccess={() => {
            fetchPurchases();
            setShowAddPaymentModal(false);
          }}
        />
      )}

      {showViewPaymentsModal && selectedPurchase && (
        <ViewPaymentsModal
          show={showViewPaymentsModal}
          onClose={() => setShowViewPaymentsModal(false)}
          transaction={selectedPurchase}
          onPaymentDeleted={fetchPurchases}
        />
      )}

      {showUpdateStatusModal && selectedPurchase && (
        <UpdatePurchaseStatusModal
          show={showUpdateStatusModal}
          onClose={() => setShowUpdateStatusModal(false)}
          purchase={selectedPurchase}
          onSuccess={fetchPurchases}
        />
      )}

      {showNotificationModal && selectedPurchase && (
        <PurchaseNotificationModal
          purchase={selectedPurchase}
          onClose={() => setShowNotificationModal(false)}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
};

export default ListPurchases;
